import {Component, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {NbDialogRef, NbDialogService} from '@nebular/theme';
import {SubscribedComponent} from '../subscribed.component';
import {TimeApprovalRequest, TimeTrackingService, TrackedStatus} from '../shared/time-tracking.service';
import {AuthService} from '../shared/auth.service';
import {DualAxisPagingMode, Globals} from '../shared/Globals';
import {activeFilterKeys, DualAxisDelegate, DualAxisEvent} from '../dual-axis/dual-axis.component';
import {ColDef, ColType} from '../interfaces/colDef';
import {TreeNode} from '../interfaces/treenode';
import {TableOptions} from '../settings/table-options/table-options.component';
import {Paged} from '../interfaces/paged';
import {PopupDialogComponent} from '../dialogs/popup-dialog/popup-dialog.component';
import {TimeApprovalDetailComponent} from './time-approval-detail.component';
import {ActivatedRoute} from '@angular/router';
import {Subject} from 'rxjs';

@Component({
  selector: 'bst-pending-approvals',
  templateUrl: './pending-approvals.component.html',
  styleUrls: ['./pending-approvals.component.scss'],
  standalone: false
})
export class PendingApprovalsComponent extends SubscribedComponent implements OnInit, DualAxisDelegate<TimeApprovalRequest> {
  Globals = Globals;
  TrackedStatus = TrackedStatus;
  DualAxisEvent = DualAxisEvent;

  // Reuses the app-standard bst-dual-axis table. Status is driven by the
  // nb-route-tabset (Pending/Approved/All), exactly like Timesheet/Market. The
  // remaining filters (employee/week/projects/team) live in the Table Options
  // dialog (bst-table-options-button). Clicking a row opens the approval-specific
  // detail (entries + subtotals + event log + Approve/Reject) as a full-screen
  // PopupDialog — see selectRow(). The card template stacks the same row for mobile.
  @ViewChild('cellTemplate', {static: true, read: TemplateRef}) cellTemplateOutlet: TemplateRef<any>;
  @ViewChild('cardTemplate', {static: true, read: TemplateRef}) cardTemplateOutlet: TemplateRef<any>;

  // Route-driven status tabs — the platform's standard list-page header (same
  // nb-route-tabset Timesheet/Market use). The active tab maps to the status the
  // queue is filtered by; see statusForFilter().
  tabs = [
    {title: 'Pending', route: '/time-approvals/pending'},
    {title: 'Approved', route: '/time-approvals/approved'},
    {title: 'Rejected', route: '/time-approvals/rejected'},
    {title: 'All', route: '/time-approvals/all'},
  ];

  data: TreeNode<TimeApprovalRequest>[] = [];
  stickyColumnsLeft: ColDef[] = [];
  stickyColumnsRight: ColDef[] = [];
  // Filterable columns expose a funnel in the ⋮ dialog. 'team' is filter-only
  // (hidden in the grid — the request payload has no team to display), so it's a
  // hidden SELECT column whose list is filled from the server's filter options.
  standardColumns: ColDef[] = [
    {name: 'employee', title: 'Employee', type: ColType.SELECT, required: false, filter: true, sort: false, list: []},
    {name: 'week', title: 'Week', type: ColType.DATE, required: false, filter: true, sort: false},
    {name: 'hours', title: 'Hours', type: ColType.STRING, required: false, filter: false, sort: false},
    {name: 'billable', title: 'Billable', type: ColType.STRING, required: false, filter: false, sort: false},
    {name: 'projects', title: 'Projects', type: ColType.SELECT, required: false, filter: true, sort: false, list: []},
    {name: 'team', title: 'Team', type: ColType.SELECT, required: false, filter: true, sort: false, list: []},
    // Status is a display-only chip — the tabs drive which statuses are shown, so no funnel here.
    {name: 'status', title: 'Status', type: ColType.STRING, required: false, filter: false, sort: false},
    {name: 'submitted', title: 'Submitted', type: ColType.DATE, required: false, filter: false, sort: false},
  ];
  options: TableOptions = {
    display: [
      {col: 'employee', shown: true},
      {col: 'week', shown: true},
      {col: 'hours', shown: true},
      {col: 'billable', shown: true},
      {col: 'projects', shown: true},
      {col: 'team', shown: false},
      {col: 'status', shown: true},
      {col: 'submitted', shown: true},
    ],
    others: {},
    filter: {},
    sorts: [],
  };
  events: Subject<DualAxisEvent> = new Subject<DualAxisEvent>();
  count: number = null;

  currentUserId: number = null;

  // Set from the "Review timesheet" email deep link (/time-approvals/all?request=<id>):
  // once the queue has loaded, auto-open that week's detail so the reviewer lands on
  // it (entries + Approve/Reject) without hunting. Opens at most once per visit.
  private deepLinkRequestId: number = null;
  private deepLinkOpened = false;

  constructor(private time: TimeTrackingService, private auth: AuthService, private route: ActivatedRoute,
              private dialogService: NbDialogService) {
    super();
  }

  // Active-filter count for the Table Options funnel badge (status/tab-driven keys excluded).
  public get filterCount(): number { return activeFilterKeys(this.options?.filter).length; }

  ngOnInit(): void {
    super.ngOnInit();
    this.currentUserId = this.auth.user.getValue()?.id ?? null;

    // Deep link from the submit email — only a positive integer id is honoured.
    const reqParam = this.route.snapshot.queryParamMap.get('request');
    const reqId = reqParam !== null ? Number(reqParam) : NaN;
    this.deepLinkRequestId = Number.isInteger(reqId) && reqId > 0 ? reqId : null;

    // The active tab sets the status filter and reloads. The first emission fires
    // during init (before the child dual-axis bootstraps), so its RESET is a no-op
    // and bootstrap loads with the correct status; later tab clicks reload via RESET.
    this.subs.push(this.route.paramMap.subscribe(params => {
      this.options.filter = {...this.options.filter, status: this.statusForFilter(params.get('filter'))};
      this.events.next(DualAxisEvent.RESET);
    }));
  }

  // Map the tab route segment to the status the queue filters by. Default (and any
  // unknown value) is the pending review queue — '' is the server's pending default.
  private statusForFilter(filter: string | null): string | number {
    switch (filter) {
      case 'approved': return TrackedStatus.Approved;
      case 'rejected': return TrackedStatus.Rejected;
      case 'all': return 'all';
      default: return '';
    }
  }

  // Review queue: fetches a paged result set from the server.
  // Filters come from this.options.filter, set by the dual-axis ⋮ filter dialog.
  async paged(page: number = 1, pageSize: number = 25): Promise<Paged<TimeApprovalRequest>> {
    const f = this.options.filter || {};
    this.loading.next(true);
    try {
      const res = await this.time.listApprovals(page, {
        status: f.status ?? '',                       // unset -> '' -> pending (server default)
        user_id: f.employee || undefined,
        // The DATE filter dialog emits {gte, lte} under the column key ('week'); the
        // backend snaps a single date to its Monday, so pass the range start.
        week_start: f.week?.gte || undefined,
        department_id: f.team || undefined,
        project_id: f.projects || undefined,
      }, pageSize);
      if (!res) { return null; }
      const pagedRequests = res.requests;
      this.applyFilterOptions(res.options);
      this.data = (pagedRequests.data || []).map(r => (<TreeNode<TimeApprovalRequest>>{data: r, expanded: false, children: []}));
      this.count = pagedRequests.total;
      this.maybeOpenDeepLink();
      return pagedRequests;
    } finally {
      this.loading.next(false);
    }
  }

  // Fill the SELECT filter lists from the server's distinct options, so the ⋮
  // employee/team/project dropdowns are populated when the user opens them.
  private applyFilterOptions(opts: {employees: any[], teams: any[], projects: any[]} | undefined) {
    if (!opts) { return; }
    const col = (name: string) => this.standardColumns.find(c => c.name === name);
    if (col('employee')) { col('employee').list = opts.employees || []; }
    if (col('team')) { col('team').list = opts.teams || []; }
    if (col('projects')) { col('projects').list = opts.projects || []; }
  }

  // Clicking a row opens the week detail as a full-screen dialog (same drill-down
  // pattern as the Clients/Market preview). The overview stays a compact list; the
  // detail owns the entries table, subtotals, event log and Approve/Reject. `onDecision`
  // is a closure over the dialog ref (assigned by open()), so the detail can close the
  // dialog after a decision; the queue reloads on close via the RESET event.
  selectRow(_ev: MouseEvent, node: TreeNode<TimeApprovalRequest>) {
    this.openDetail(node);
  }

  // Open the deep-linked week's detail once the queue has loaded (see
  // deepLinkRequestId). No-op when there's no deep link or it's already been
  // opened. The id is usually on the loaded page, but pagination means the
  // loaded page is no longer the reviewer's whole queue, so a miss falls back
  // to fetching that one request directly (still a no-op if canView() denies it).
  private async maybeOpenDeepLink(): Promise<void> {
    if (this.deepLinkOpened || !this.deepLinkRequestId) { return; }
    const node = (this.data || []).find(n => n.data?.id === this.deepLinkRequestId);
    if (node) {
      this.deepLinkOpened = true;
      this.openDetail(node);
      return;
    }
    const request = await this.time.getApproval(this.deepLinkRequestId);
    if (!request) { return; }
    this.deepLinkOpened = true;
    this.openDetail(<TreeNode<TimeApprovalRequest>>{data: request, expanded: false, children: []});
  }

  private openDetail(node: TreeNode<TimeApprovalRequest>) {
    let ref: NbDialogRef<PopupDialogComponent<TimeApprovalDetailComponent>>;
    ref = this.dialogService.open<PopupDialogComponent<TimeApprovalDetailComponent>>(PopupDialogComponent, {
      closeOnBackdropClick: false,
      // Short content — collapse the shared popup card to its content (see the
      // .approval-detail-dialog rule in styles.scss), instead of the default
      // height:100% stretch that orphans the Close footer under an empty body.
      dialogClass: 'approval-detail-dialog',
      context: {
        comp: TimeApprovalDetailComponent,
        componentContext: {
          request: node.data,
          currentUserId: this.currentUserId,
          onDecision: () => ref?.close(),
        }
      }
    });
    this.subs.push(ref.onClose.subscribe(() => this.events.next(DualAxisEvent.REFRESH)));
  }

  employeeName(r: TimeApprovalRequest): string {
    // Fall back to the login (email) when the profile has no name parts, so the
    // row never renders a blank employee cell.
    return [r.employee?.name_first, r.employee?.name_middle, r.employee?.name_last].filter(Boolean).join(' ')
      || r.employee?.username || '';
  }

  statusLabel(status: TrackedStatus): string {
    switch (status) {
      case TrackedStatus.Submitted: return 'Pending';
      case TrackedStatus.Approved: return 'Approved';
      case TrackedStatus.Rejected: return 'Rejected';
      default: return 'Draft';
    }
  }

  totalSeconds(r: TimeApprovalRequest): number {
    return (r.entries || []).reduce((sum, e) => sum + (e.tracked_duration || 0), 0);
  }

  billableSeconds(r: TimeApprovalRequest): number {
    return (r.entries || [])
      .filter(e => e.user_to_phase?.phase?.project?.is_billable)
      .reduce((sum, e) => sum + (e.tracked_duration || 0), 0);
  }

  projectNames(r: TimeApprovalRequest): string {
    return (r.entries || [])
      .map(e => e.user_to_phase?.phase?.project?.name)
      .filter((v, i, a) => v && a.indexOf(v) === i)
      .join(', ');
  }

  duration(seconds: number): string {
    const hours = Math.floor((seconds || 0) / 3600);
    const minutes = Math.round(((seconds || 0) % 3600) / 60);
    return `${hours}h ${minutes.toString().padStart(2, '0')}m`;
  }

  get headerTitle(): string | { title: string; translate: boolean | Object } {
    return 'Approvals'
  }
}
